import { Schemas } from '@api-client/generated/types';

type PaginationNormalizer<T> = {
  records: T[];
  prevData: T[];
  currentPage: number;
  scrollPage: number;
  metadata?: Schemas.PaginationMetadata;
  setCurrentPage: (page: number) => void;
};

/**
 * Resolves the next page to fetch given how many pages are already loaded.
 *
 * @param loadedPage - Highest page number already loaded.
 * @param totalPages - Total number of pages available.
 * @returns The next page number, or `null` when everything is loaded.
 */
export const getNextPage = (
  loadedPage: number,
  totalPages: number
): number | null => (loadedPage < totalPages ? loadedPage + 1 : null);

/**
 * Flattens a page-keyed record map into a single ordered list, sorting by
 * page number so earlier pages always come before later ones.
 */
export const flattenPagedData = <T>(pages: Record<number, T[]>): T[] =>
  Object.keys(pages)
    .sort((a, b) => Number(a) - Number(b))
    .flatMap((key) => pages[Number(key)]);

/**
 * Drops every loaded page after `page`, keeping only pages up to and
 * including it. Used to flush the tail when jumping back to an earlier page.
 */
export const flushPagesAfter = <T>(
  pages: Record<number, T[]>,
  page: number
): Record<number, T[]> =>
  Object.fromEntries(
    Object.entries(pages).filter(([key]) => Number(key) <= page)
  );

export const paginationNormalizer = <T>({
  metadata,
  records,
  prevData,
  currentPage,
  scrollPage,
  setCurrentPage,
}: PaginationNormalizer<T>) => {
  if (metadata?.totalRecords === 0) {
    return [];
  }

  if (!records.length) {
    if (scrollPage === 1) {
      return records;
    }
  } else {
    setCurrentPage(metadata?.currentPage || 1);

    if (scrollPage === 1) {
      return records;
    } else if (currentPage === scrollPage) {
      return prevData;
    } else {
      return [...prevData, ...records];
    }
  }
};
